import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Trash2, Edit2, Save, X, FileText, Users, 
  Layers, ClipboardList, BookOpen, Calendar, CheckCircle, Settings
} from 'lucide-react';
import api from '../utils/api';

const AssessmentConfiguration = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('types');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [sections, setSections] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [terms, setTerms] = useState([]);
  const [boards, setBoards] = useState([]);
  const [grades, setGrades] = useState([]);
  const [students, setStudents] = useState([]);
  
  // Modal states
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  
  // Form states
  const [typeForm, setTypeForm] = useState({
    name: '', code: '', has_paper_structure: false, has_ao_tracking: false, description: ''
  });
  const [sectionForm, setSectionForm] = useState({ name: '', grade_id: '' });
  const [assessmentForm, setAssessmentForm] = useState({
    name: '', assessment_type_id: '', term_id: '', board_id: '', grade_id: '', 
    subject_config_id: '', max_marks: 100, date: ''
  });
  const [resultForm, setResultForm] = useState({
    student_id: '', assessment_id: '', subject_config_id: '', section_id: '',
    marks_obtained: '', max_marks: 100
  });

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [typesRes, sectionsRes, assessmentsRes, subjectsRes, termsRes, boardsRes, gradesRes, studentsRes] = await Promise.all([
        api.get('/igcse/assessment-types'),
        api.get('/igcse/sections'),
        api.get('/igcse/assessments'),
        api.get('/igcse/subjects'),
        api.get('/igcse/terms'),
        api.get('/boards'),
        api.get('/grades'),
        api.get('/school-admin/students').catch(() => api.get('/admin/students'))
      ]);
      setAssessmentTypes(typesRes.data);
      setSections(sectionsRes.data);
      setAssessments(assessmentsRes.data);
      setSubjects(subjectsRes.data);
      setTerms(termsRes.data);
      setBoards(boardsRes.data);
      setGrades(gradesRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Assessment Type handlers
  const handleCreateType = async (e) => {
    e.preventDefault();
    try {
      await api.post('/igcse/assessment-types', typeForm);
      setShowTypeModal(false);
      setTypeForm({ name: '', code: '', has_paper_structure: false, has_ao_tracking: false, description: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error creating assessment type');
    }
  };

  const handleDeleteType = async (id) => {
    if (!window.confirm('Delete this assessment type?')) return;
    try {
      await api.delete(`/igcse/assessment-types/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting assessment type');
    }
  };

  // Section handlers
  const handleCreateSection = async (e) => {
    e.preventDefault();
    try {
      await api.post('/igcse/sections', sectionForm);
      setShowSectionModal(false);
      setSectionForm({ name: '', grade_id: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error creating section');
    }
  };

  const handleDeleteSection = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    try {
      await api.delete(`/igcse/sections/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting section');
    }
  };

  // Assessment handlers
  const handleCreateAssessment = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...assessmentForm,
        max_marks: assessmentForm.max_marks ? parseInt(assessmentForm.max_marks) : null,
        term_id: assessmentForm.term_id || null,
        board_id: assessmentForm.board_id || null,
        grade_id: assessmentForm.grade_id || null,
        subject_config_id: assessmentForm.subject_config_id || null,
      };
      await api.post('/igcse/assessments', payload);
      setShowAssessmentModal(false);
      setAssessmentForm({
        name: '', assessment_type_id: '', term_id: '', board_id: '', grade_id: '',
        subject_config_id: '', max_marks: 100, date: ''
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error creating assessment');
    }
  };

  const handleDeleteAssessment = async (id) => {
    if (!window.confirm('Delete this assessment?')) return;
    try {
      await api.delete(`/igcse/assessments/${id}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting assessment');
    }
  };

  // Result handlers
  const handleRecordResult = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        student_id: resultForm.student_id,
        assessment_id: resultForm.assessment_id,
        subject_config_id: resultForm.subject_config_id,
        section_id: resultForm.section_id || null,
        simple_score: {
          marks_obtained: parseFloat(resultForm.marks_obtained),
          max_marks: parseInt(resultForm.max_marks)
        }
      };
      await api.post('/igcse/assessment-results', payload);
      setShowResultModal(false);
      setResultForm({
        student_id: '', assessment_id: '', subject_config_id: '', section_id: '',
        marks_obtained: '', max_marks: 100
      });
      alert('Result recorded successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error recording result');
    }
  };

  // Get badge color for assessment type
  const getTypeBadgeColor = (code) => {
    const colors = {
      'CT': 'bg-purple-100 text-purple-700 border-purple-200',
      'UT': 'bg-blue-100 text-blue-700 border-blue-200',
      'TE': 'bg-green-100 text-green-700 border-green-200',
      'ME': 'bg-orange-100 text-orange-700 border-orange-200'
    };
    return colors[code] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Assessment Configuration</h1>
              <p className="text-sm text-gray-500">Manage assessment types, sections, and record results</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2">
            {[
              { id: 'types', label: 'Assessment Types', icon: Layers },
              { id: 'sections', label: 'Sections', icon: Users },
              { id: 'assessments', label: 'Assessments', icon: ClipboardList },
              { id: 'results', label: 'Record Results', icon: CheckCircle },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id 
                    ? 'bg-indigo-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Assessment Types Tab */}
        {activeTab === 'types' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Assessment Types</h2>
              <button
                onClick={() => setShowTypeModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                data-testid="add-type-btn"
              >
                <Plus className="w-4 h-4" />
                Add Type
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {assessmentTypes.map(type => (
                <div key={type.id} className="bg-white rounded-xl p-4 shadow-sm border">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className={`inline-block px-2 py-1 text-xs font-bold rounded border ${getTypeBadgeColor(type.code)}`}>
                        {type.code}
                      </span>
                      <h3 className="mt-2 font-semibold text-gray-900">{type.name}</h3>
                    </div>
                    <button
                      onClick={() => handleDeleteType(type.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${type.has_paper_structure ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                      <span className="text-gray-600">Paper Structure: {type.has_paper_structure ? 'Yes' : 'No'}</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${type.has_ao_tracking ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                      <span className="text-gray-600">AO Tracking: {type.has_ao_tracking ? 'Yes' : 'No'}</span>
                    </p>
                  </div>
                  {type.description && (
                    <p className="mt-2 text-xs text-gray-500">{type.description}</p>
                  )}
                </div>
              ))}
              
              {assessmentTypes.length === 0 && (
                <div className="col-span-full bg-white rounded-xl p-8 text-center border">
                  <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No assessment types configured</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sections Tab */}
        {activeTab === 'sections' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Sections</h2>
              <button
                onClick={() => setShowSectionModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                data-testid="add-section-btn"
              >
                <Plus className="w-4 h-4" />
                Add Section
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {sections.map(section => (
                <div key={section.id} className="bg-white rounded-xl p-4 shadow-sm border text-center">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-xl font-bold text-indigo-600">{section.name.charAt(section.name.length - 1)}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900">{section.name}</h3>
                  <button
                    onClick={() => handleDeleteSection(section.id)}
                    className="mt-2 text-xs text-red-500 hover:text-red-700"
                  >
                    Delete
                  </button>
                </div>
              ))}
              
              {sections.length === 0 && (
                <div className="col-span-full bg-white rounded-xl p-8 text-center border">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No sections created</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Assessments Tab */}
        {activeTab === 'assessments' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Assessments</h2>
              <button
                onClick={() => setShowAssessmentModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                data-testid="add-assessment-btn"
              >
                <Plus className="w-4 h-4" />
                Create Assessment
              </button>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Assessment</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Subject</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Term</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Max Marks</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assessments.map(assessment => (
                    <tr key={assessment.id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{assessment.name}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-1 text-xs font-bold rounded border ${getTypeBadgeColor(assessment.assessment_type_code)}`}>
                          {assessment.assessment_type_code}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{assessment.subject_name || 'All Subjects'}</td>
                      <td className="px-4 py-3 text-gray-600">{assessment.term_name || '-'}</td>
                      <td className="px-4 py-3 text-center text-gray-600">{assessment.max_marks || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{assessment.date || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDeleteAssessment(assessment.id)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {assessments.length === 0 && (
                <div className="p-8 text-center">
                  <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No assessments created</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Record Results Tab */}
        {activeTab === 'results' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Record Student Results</h2>
              <button
                onClick={() => setShowResultModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                data-testid="record-result-btn"
              >
                <Plus className="w-4 h-4" />
                Record Result
              </button>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-indigo-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Record Assessment Results</h3>
                <p className="text-gray-500 mb-4">Click "Record Result" to enter student scores for assessments</p>
                <div className="flex justify-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <FileText className="w-4 h-4" />
                    {assessments.length} Assessments
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {students.length} Students
                  </span>
                  <span className="flex items-center gap-1">
                    <BookOpen className="w-4 h-4" />
                    {subjects.length} Subjects
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Assessment Type Modal */}
      {showTypeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Add Assessment Type</h3>
              <button onClick={() => setShowTypeModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateType} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={typeForm.name}
                  onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., Class Test"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input
                  type="text"
                  value={typeForm.code}
                  onChange={(e) => setTypeForm({ ...typeForm, code: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., CT"
                  maxLength={3}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={typeForm.description}
                  onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Optional description"
                />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={typeForm.has_paper_structure}
                    onChange={(e) => setTypeForm({ ...typeForm, has_paper_structure: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Paper Structure (P1-P6)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={typeForm.has_ao_tracking}
                    onChange={(e) => setTypeForm({ ...typeForm, has_ao_tracking: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">AO Tracking</span>
                </label>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowTypeModal(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Section Modal */}
      {showSectionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Add Section</h3>
              <button onClick={() => setShowSectionModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateSection} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Section Name</label>
                <input
                  type="text"
                  value={sectionForm.name}
                  onChange={(e) => setSectionForm({ ...sectionForm, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., Section A"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade (Optional)</label>
                <select
                  value={sectionForm.grade_id}
                  onChange={(e) => setSectionForm({ ...sectionForm, grade_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Grades</option>
                  {grades.map(grade => (
                    <option key={grade.id} value={grade.id}>{grade.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowSectionModal(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assessment Modal */}
      {showAssessmentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Create Assessment</h3>
              <button onClick={() => setShowAssessmentModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateAssessment} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assessment Name *</label>
                <input
                  type="text"
                  value={assessmentForm.name}
                  onChange={(e) => setAssessmentForm({ ...assessmentForm, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., Unit Test 1, Term 1 Exam"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assessment Type *</label>
                <select
                  value={assessmentForm.assessment_type_id}
                  onChange={(e) => setAssessmentForm({ ...assessmentForm, assessment_type_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select Type</option>
                  {assessmentTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name} ({type.code})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
                  <select
                    value={assessmentForm.term_id}
                    onChange={(e) => setAssessmentForm({ ...assessmentForm, term_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select Term</option>
                    {terms.map(term => (
                      <option key={term.id} value={term.id}>{term.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Board</label>
                  <select
                    value={assessmentForm.board_id}
                    onChange={(e) => setAssessmentForm({ ...assessmentForm, board_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select Board</option>
                    {boards.map(board => (
                      <option key={board.id} value={board.id}>{board.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                  <select
                    value={assessmentForm.grade_id}
                    onChange={(e) => setAssessmentForm({ ...assessmentForm, grade_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Grades</option>
                    {grades.map(grade => (
                      <option key={grade.id} value={grade.id}>{grade.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <select
                    value={assessmentForm.subject_config_id}
                    onChange={(e) => setAssessmentForm({ ...assessmentForm, subject_config_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Subjects</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>{subject.name} ({subject.code})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Marks</label>
                  <input
                    type="number"
                    value={assessmentForm.max_marks}
                    onChange={(e) => setAssessmentForm({ ...assessmentForm, max_marks: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="100"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={assessmentForm.date}
                    onChange={(e) => setAssessmentForm({ ...assessmentForm, date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowAssessmentModal(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  Create Assessment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record Result Modal */}
      {showResultModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Record Student Result</h3>
              <button onClick={() => setShowResultModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleRecordResult} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Student *</label>
                <select
                  value={resultForm.student_id}
                  onChange={(e) => setResultForm({ ...resultForm, student_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select Student</option>
                  {students.map(student => (
                    <option key={student.id} value={student.id}>{student.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assessment *</label>
                <select
                  value={resultForm.assessment_id}
                  onChange={(e) => {
                    const assessment = assessments.find(a => a.id === e.target.value);
                    setResultForm({ 
                      ...resultForm, 
                      assessment_id: e.target.value,
                      max_marks: assessment?.max_marks || 100
                    });
                  }}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select Assessment</option>
                  {assessments.map(assessment => (
                    <option key={assessment.id} value={assessment.id}>
                      {assessment.name} ({assessment.assessment_type_code})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <select
                  value={resultForm.subject_config_id}
                  onChange={(e) => setResultForm({ ...resultForm, subject_config_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select Subject</option>
                  {subjects.map(subject => (
                    <option key={subject.id} value={subject.id}>{subject.name} ({subject.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
                <select
                  value={resultForm.section_id}
                  onChange={(e) => setResultForm({ ...resultForm, section_id: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">No Section</option>
                  {sections.map(section => (
                    <option key={section.id} value={section.id}>{section.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marks Obtained *</label>
                  <input
                    type="number"
                    value={resultForm.marks_obtained}
                    onChange={(e) => setResultForm({ ...resultForm, marks_obtained: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter marks"
                    min="0"
                    max={resultForm.max_marks}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Marks</label>
                  <input
                    type="number"
                    value={resultForm.max_marks}
                    onChange={(e) => setResultForm({ ...resultForm, max_marks: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="100"
                    min="1"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowResultModal(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  Record Result
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentConfiguration;
